import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * BUILDING ACTIVITIES — the OUT half.
 *
 * The IN half is the daily-log wizard and posts through the shared request
 * helpers like every other form. These two are the close: what is still open at
 * a desk, and closing one. They live in their own client rather than beside the
 * wizard's calls because they are the only two calls on this table that are not
 * a form submission — see src/CRUD/daily-log.js for the routes and migration
 * 079 for the columns behind them.
 *
 * Uses the DEFAULT axios instance, so both calls inherit the interceptors from
 * axios-setup.ts: withCredentials for the httpOnly session cookies, the
 * X-CSRF-Token double-submit header, and the single-flight 401 -> refresh ->
 * retry. A bare axios.create() here would silently lose all three.
 */

/** One row of the Out picker. Deliberately the six fields the row renders and
 *  nothing else — the server sends this shape, not a whole log entry. */
export interface OpenActivity {
  id: number;
  buildingActivity: string;
  apartmentLocation: string | null;
  details: string | null;
  priority: string | null;
  createdAt: string;
}

interface CloseErrorBody {
  message?: string;
  code?: string | null;
  closed_at?: string | null;
  close_source?: string | null;
}

/**
 * Carries the server's own answer, not a rephrasing of it. A 409 means the
 * activity was ALREADY closed and the body says when and by what kind of act —
 * which the screen needs, because "somebody beat you to it" and "the sweep
 * tidied it up" are different things to tell the person at the desk.
 */
export class ActivityCloseError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null,
    readonly closedAt: string | null,
    readonly closeSource: string | null
  ) {
    super(message);
    this.name = 'ActivityCloseError';
  }
}

/**
 * What is still open at ONE building. propertyId is required by the server and
 * is not a convenience: without it the question is "every open activity in every
 * building you can see", which on a portfolio account is a list nobody can act
 * on.
 */
export const getOpenActivities = async (
  propertyId: number
): Promise<OpenActivity[]> => {
  const { data } = await axios.get(`${apiRoute}/daily-log/open`, {
    params: { propertyId },
  });

  return Array.isArray(data) ? data : [];
};

export const closeActivity = async (id: number): Promise<void> => {
  try {
    await axios.post(`${apiRoute}/daily-log/${id}/close`);
  } catch (e) {
    const err = e as AxiosError<CloseErrorBody>;
    const body = err.response?.data;
    const status = err.response?.status ?? null;

    throw new ActivityCloseError(
      body?.message ||
        (status === 404
          ? 'That activity is not on this building any more. Reload the list.'
          : 'Could not close the activity.'),
      status,
      body?.code ?? null,
      body?.closed_at ?? null,
      body?.close_source ?? null
    );
  }
};
